import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import { processingApi } from "../../api/processingApi";
import { ProcessingLine, ProcessingReport } from "../../types/processing.type";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (value: number) => (Number.isFinite(value) ? value.toFixed(2) : "—");
const money = (value: number) => (Number.isFinite(value) ? value.toFixed(4) : "—");

export default function ProcessingReports() {
  const { authState } = useAuth();
  const isAdmin = authState.user?.role === UserRole.Admin;

  const [reports, setReports] = useState<ProcessingReport[]>([]);
  const [lines, setLines] = useState<ProcessingLine[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(30);

  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [lineId, setLineId] = useState<string | undefined>(undefined);

  const [loading, setLoading] = useState<boolean>(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { loading: exporting, downloadFile } = useDownloadFile();

  // Выгрузка берёт те же фильтры, что и журнал на экране: что видишь — то и в файле.
  const handleExport = () => {
    const startDate = range?.[0]?.format("YYYY-MM-DD");
    const endDate = range?.[1]?.format("YYYY-MM-DD");
    const fileName = `pererabotka_${startDate || "vse"}_${endDate || dayjs().format("YYYY-MM-DD")}.xlsx`;
    downloadFile("/processing/reports/excel", fileName, { lineId, startDate, endDate }).catch(() => {});
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await processingApi.getReports({
        page,
        limit,
        lineId,
        startDate: range?.[0]?.format("YYYY-MM-DD"),
        endDate: range?.[1]?.format("YYYY-MM-DD"),
      });
      setReports(data.reports || []);
      setTotal(data.total || 0);
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось загрузить журнал переработки.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, lineId, range]);

  useEffect(() => {
    processingApi
      .getLines()
      .then((data) => setLines(data.lines || []))
      .catch(() => setLines([]));
  }, []);

  const handleDelete = async (report: ProcessingReport) => {
    setDeletingId(report._id);
    try {
      const result = await processingApi.deleteReport(report._id);
      message.success(result.message || "Загрузка откачена.");
      (result.warnings || []).forEach((warning) => message.warning(warning, 10));
      fetchReports();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось откатить загрузку.", 10);
    } finally {
      setDeletingId(null);
    }
  };

  const columns = useMemo(
    () => [
      { title: "Номер", dataIndex: "number", key: "number", width: 110 },
      { title: "Дата", dataIndex: "date", key: "date", width: 150 },
      { title: "Завсклад", dataIndex: "warehouseMgrName", key: "warehouseMgrName" },
      { title: "Линия", dataIndex: "lineName", key: "lineName" },
      {
        title: "Вход",
        key: "input",
        width: 140,
        render: (_: any, r: ProcessingReport) => (
          <Text>
            {fmt(r.input.quantity)} {r.input.unitOfMeasurement}
          </Text>
        ),
      },
      {
        title: "Потеря",
        key: "loss",
        width: 180,
        render: (_: any, r: ProcessingReport) => (
          <Space size={6}>
            <Text>
              {fmt(r.loss.quantity)} · {fmt(r.loss.percent)} %
            </Text>
            {r.loss.status === "Превышена" ? (
              <Tag color="error">выше {r.loss.limitPercent} %</Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: "Статус",
        key: "status",
        width: 150,
        render: (_: any, r: ProcessingReport) => {
          const off = r.outputs.some((o) => o.status !== "В норме");
          if (r.loss.status === "Превышена" || off) {
            return <Tag color="warning">С отклонением</Tag>;
          }
          if (r.warnings && r.warnings.length > 0) {
            return <Tag color="warning">С предупреждением</Tag>;
          }
          return <Tag color="success">В норме</Tag>;
        },
      },
      ...(isAdmin
        ? [
            {
              title: "",
              key: "actions",
              width: 130,
              render: (_: any, r: ProcessingReport) => (
                <Popconfirm
                  title="Откатить загрузку?"
                  description="Выходы будут сняты со склада, а вход вернётся в свои партии. Действие необратимо."
                  okText="Откатить"
                  cancelText="Отмена"
                  onConfirm={() => handleDelete(r)}
                >
                  <Button danger type="link" icon={<DeleteOutlined />} loading={deletingId === r._id}>
                    Откатить
                  </Button>
                </Popconfirm>
              ),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, deletingId]
  );

  /** Раскрытая строка: полная раскладка загрузки с себестоимостью. */
  const renderDetails = (report: ProcessingReport) => (
    <div style={{ padding: "8px 4px" }}>
      {report.warnings && report.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Загрузка прошла с послаблением"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {report.warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          }
        />
      )}

      <Descriptions size="small" column={3} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Загружено">
          {fmt(report.input.quantity)} {report.input.unitOfMeasurement} — {report.input.itemName}
        </Descriptions.Item>
        <Descriptions.Item label="Себестоимость входа">
          {money(report.input.cost.USD)} $ / {money(report.input.cost.UZS)} сум
        </Descriptions.Item>
        <Descriptions.Item label="Разноска">
          {report.costAllocation === "salesValue" ? "По стоимости продажи" : "По массе"}
        </Descriptions.Item>
      </Descriptions>

      <Table
        size="small"
        pagination={false}
        rowKey={(o: any) => o.itemId}
        dataSource={report.outputs}
        columns={[
          { title: "Выход", dataIndex: "itemName", key: "itemName" },
          {
            title: "Кол-во",
            key: "quantity",
            render: (_: any, o: any) => `${fmt(o.quantity)} ${o.unitOfMeasurement}`,
          },
          {
            title: "Факт / норма",
            key: "percent",
            render: (_: any, o: any) => (
              <Space size={6}>
                <Text>
                  {fmt(o.percent)} % / {o.normPercent} %
                </Text>
                {o.status !== "В норме" && <Tag color="warning">{o.status}</Tag>}
              </Space>
            ),
          },
          {
            title: "Себест. за ед.",
            key: "unitCost",
            render: (_: any, o: any) => `${money(o.unitCost.USD)} $`,
          },
          {
            title: "Себест. всего",
            key: "allocatedCost",
            render: (_: any, o: any) => `${money(o.allocatedCost.USD)} $`,
          },
          {
            title: "Цена на момент",
            key: "salePrice",
            render: (_: any, o: any) => `${money(o.salePrice.USD)} $`,
          },
        ]}
      />

      <Alert
        type="info"
        style={{ marginTop: 12 }}
        message={
          <span>
            Потеря: <b>{fmt(report.loss.quantity)}</b> ({fmt(report.loss.percent)} %) при потолке{" "}
            {report.loss.limitPercent} %. Себестоимость — <b>ноль</b>: килограммы числятся,
            деньги распределены по товарным выходам. Справочно угар обошёлся в{" "}
            {money(report.loss.costEquivalent.USD)} $.
          </span>
        }
      />
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Журнал переработки</Title>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            format="DD.MM.YYYY"
            value={range as any}
            onChange={(value) => {
              setRange(value as [Dayjs, Dayjs] | null);
              setPage(1);
            }}
          />
          <Select
            allowClear
            placeholder="Все линии"
            style={{ minWidth: 240 }}
            value={lineId}
            onChange={(value) => {
              setLineId(value);
              setPage(1);
            }}
            options={lines.map((l) => ({ value: l._id, label: l.name }))}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchReports} loading={loading}>
            Обновить
          </Button>
          <Button icon={<PiMicrosoftExcelLogoFill />} onClick={handleExport} loading={exporting}>
            Экспорт в Excel
          </Button>
          <Button
            onClick={() => {
              setRange([dayjs().startOf("month"), dayjs()]);
              setPage(1);
            }}
          >
            Текущий месяц
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          loading={loading}
          dataSource={reports}
          columns={columns as any}
          rowKey="_id"
          expandable={{ expandedRowRender: renderDetails }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setLimit(nextSize);
            },
          }}
        />
      </Card>
    </div>
  );
}
